import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaw,
  faCalendarAlt,
  faClock,
  faChild,
  faLeaf,
  faHorse,
  faStar,
  faTicketAlt,
  faDragon,
} from '@fortawesome/free-solid-svg-icons';
import ParallaxBanner from '../../common/Parralax-banner';
import Footer from '../../Footer/Footer';
import { useRetailSection } from '../../../hooks/useRetailSection';
import animalMap from '../../../assets/AnimalFarmMap.webp';
import animalBanner from '../../../assets/banner/AnimalBan.webp';
import ponyImage from '../../../assets/Pony.jpeg';
import SEO from '../../common/SEO';
import {
  ANIMAL_PARK_OCTOBER_HALF_TERM_2026,
  getAnimalParkOpeningDisplay,
} from '../../../utils/openingHoursUtils';

// ─── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
`;

// ─── Quick-info strip ──────────────────────────────────────────────────────────

const InfoStrip = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
`;

const InfoStripInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  divide-color: rgba(255,255,255,0.15);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.12);

  &:last-child {
    border-right: none;
  }

  @media (max-width: 640px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    &:last-child { border-bottom: none; }
  }
`;

const InfoIcon = styled.div`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.accent};
  flex-shrink: 0;
  width: 2rem;
  text-align: center;
`;

const InfoText = styled.div`
  & strong {
    display: block;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.65;
    margin-bottom: 0.15rem;
    font-weight: 600;
  }
  & span {
    font-size: 1rem;
    font-weight: 600;
  }
`;

// ─── Intro ─────────────────────────────────────────────────────────────────────

const IntroSection = styled.section`
  padding: 5rem 0 3rem;
  text-align: center;
`;

const EyebrowLabel = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
  margin-bottom: 1.5rem;
`;

const IntroBody = styled.p`
  font-size: 1.15rem;
  line-height: 1.85;
  color: ${props => props.theme.colors.secondary};
  max-width: 760px;
  margin: 0 auto 2.5rem;
`;

const BookBtn = styled.a`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-size: 1.05rem;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease,
    box-shadow 0.25s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const IntroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  align-items: center;
`;

const SecondaryBookBtn = styled.a`
  display: inline-block;
  background-color: transparent;
  color: ${props => props.theme.colors.primary};
  font-size: 1.05rem;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  border: 2px solid ${props => props.theme.colors.primary};
  transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

// ─── Divider ───────────────────────────────────────────────────────────────────

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin: 0;
`;

// ─── Experiences section ───────────────────────────────────────────────────────

const ExperiencesSection = styled.section`
  padding: 5rem 0;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;

const ExperiencesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const ExperienceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.25rem 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const CardIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background-color: ${props => props.theme.colors.primary}12;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.25rem;
`;

const CardTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.6rem;
`;

const CardText = styled.p`
  font-size: 1rem;
  line-height: 1.65;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;

// ─── Pony ride days callout ────────────────────────────────────────────────────

const MayFourthSection = styled.section`
  padding: 5rem 0;
`;

const MayFourthInner = styled.div`
  background: ${props => props.theme.colors.primary};
  border-radius: 16px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 360px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MayFourthContent = styled.div`
  padding: 3.5rem 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const EventBadge = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.35rem 0.9rem;
  border-radius: 50px;
  margin-bottom: 1.25rem;
`;

const MayFourthTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  color: white;
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const MayFourthBody = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: 2rem;
`;

const MayFourthDetails = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const MayFourthDetail = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: rgba(255, 255, 255, 0.9);

  & svg {
    color: ${props => props.theme.colors.accent};
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

// ─── Halloween tease ──────────────────────────────────────────────────────────
const StuartTeaseSection = styled.section`
  padding: 0 0 3rem;
`;

const StuartTeaseWrap = styled.div`
  text-align: center;
  padding: clamp(1.5rem, 3.5vw, 2.25rem) clamp(1.5rem, 4vw, 3rem);
  background: linear-gradient(135deg, rgba(123, 63, 158, 0.1), rgba(224, 122, 47, 0.08));
  border: 1.5px solid rgba(123, 63, 158, 0.28);
  border-radius: 16px;
`;

const StuartTeaseLine = styled.p`
  margin: 0 0 0.75rem;
  font-size: clamp(1.15rem, 2.5vw, 1.45rem);
  font-weight: 700;
  line-height: 1.35;
  color: #4a2d5c;
`;

const StuartTeaseBody = styled.p`
  margin: 0 0 1.25rem;
  max-width: 52rem;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1.25rem;
  font-size: clamp(0.98rem, 1.8vw, 1.1rem);
  line-height: 1.65;
  color: #5a3d6e;
`;

const StuartTeaseBtn = styled.a`
  display: inline-block;
  background: #7b3f9e;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 0.9rem 1.85rem;
  border-radius: 999px;
  border: 2px solid #7b3f9e;
  box-shadow: 0 4px 16px rgba(123, 63, 158, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    background: transparent;
    color: #7b3f9e;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(123, 63, 158, 0.22);
  }
`;

const WhiteBookBtn = styled.a`
  display: inline-block;
  background-color: white;
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  font-weight: 700;
  padding: 0.9rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  align-self: flex-start;
  border: 2px solid white;
  transition: background-color 0.25s ease, color 0.25s ease;

  &:hover {
    background-color: transparent;
    color: white;
  }
`;

const MayFourthVisual = styled.div`
  position: relative;
  min-height: 360px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);

  @media (max-width: 768px) {
    min-height: 260px;
  }
`;

const MayFourthImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  display: block;
`;

// ─── Reptile Day ───────────────────────────────────────────────────────────────

const ReptileSection = styled.section`
  padding: 5rem 0;
`;

const ReptileCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const ReptileLogoBanner = styled.div`
  background: #0a0a0a;
  padding: 2.5rem 2rem;
  text-align: center;

  @media (max-width: 640px) {
    padding: 1.75rem 1.25rem;
  }
`;

const ReptileLogo = styled.img`
  max-width: min(520px, 100%);
  height: auto;
  display: block;
  margin: 0 auto;
`;

const ReptileBody = styled.div`
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const ReptileBadge = styled.span`
  display: inline-block;
  background: linear-gradient(90deg, #6bb872, #5baed7);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.35rem 0.9rem;
  border-radius: 50px;
  margin-bottom: 1.25rem;
`;

const ReptileTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const ReptileIntro = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.75rem;
  max-width: 720px;
`;

const ReptileDetails = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ReptileDetail = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: ${props => props.theme.colors.secondary};

  & svg {
    color: #5baed7;
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const ReptileGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ReptileGalleryImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 10px;
  display: block;
`;

const ReptileComingSoon = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
  font-style: italic;
`;

const PonyDateList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PonyDateItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: rgba(255, 255, 255, 0.9);

  & svg {
    color: ${props => props.theme.colors.accent};
    flex-shrink: 0;
  }
`;

const PonyBookButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

// ─── Map section ───────────────────────────────────────────────────────────────

const MapSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
`;

const MapFrame = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-top: 2.5rem;
`;

const MapImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
`;

// ─── Pricing ───────────────────────────────────────────────────────────────────

const PricingSection = styled.section`
  padding: 5rem 0;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const PricingLabel = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.5rem;
`;

const PricingAmount = styled.div`
  font-size: 3.2rem;
  font-weight: 800;
  color: ${props => props.theme.colors.primary};
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const PricingPer = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.25rem;
`;

const PricingNote = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.5;
  margin-bottom: 1.75rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.07);
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const ANIMAL_PARK_BOOKING_BASE =
  '/booking-journey?locationId=978&pageId=833fe805-0f3f-4af2-a552-7e86642746ad';

const bookingUrl = (date) =>
  date ? `${ANIMAL_PARK_BOOKING_BASE}&date=${date}` : ANIMAL_PARK_BOOKING_BASE;

const BOOKING_URL = bookingUrl();

const PONY_RIDE_DATES = [
  { iso: '2026-09-12', label: 'Saturday 12 September 2026' },
];

const ponyRideDatesLabel = PONY_RIDE_DATES.map((d) => d.label.replace(' 2026', '')).join(', ');

const REPTILE_LOGO =
  'https://BelvoirCastlePull.b-cdn.net/Website/48455.jpg';
const REPTILE_IMAGES = [
  {
    src: 'https://BelvoirCastlePull.b-cdn.net/Website/48449.png',
    alt: 'Guest holding a snake at a Riverview Reptiles handling session',
  },
  {
    src: 'https://BelvoirCastlePull.b-cdn.net/Website/48450.png',
    alt: 'Child gently handling a bearded dragon',
  },
  {
    src: 'https://BelvoirCastlePull.b-cdn.net/Website/48448.png',
    alt: 'Families exploring the reptile display at an event gazebo',
  },
];

const experiences = [
  {
    icon: faPaw,
    title: 'Meet Our Animals',
    text: 'From woolly sheep and curious goats to free-range chickens and playful pigs — get up close with the animals that call Belvoir home.',
  },
  {
    icon: faChild,
    title: 'Feeding Sessions',
    text: 'Roll up your sleeves and feed the animals yourself. Guided feeding sessions are a highlight for children and grown-ups alike.',
  },
  {
    icon: faHorse,
    title: 'Pony Rides',
    text: `A very special treat on Saturday 12 September. Our gentle ponies are ready to ride between 11am and 2pm. Add them when you book.`,
  },
  {
    icon: faLeaf,
    title: 'Estate Setting',
    text: 'Set within the historic grounds of Belvoir Castle, the Animal Park enjoys a breathtaking countryside backdrop that\'s truly like nowhere else.',
  },
];

const AnimalPark = () => {
  useRetailSection();
  const openingDisplay = getAnimalParkOpeningDisplay();

  const seoDescription =
    `Belvoir Animal Park is open weekends and every day from ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}. Meet friendly farm animals, enjoy feeding sessions, and explore the park within the Belvoir Castle Retail Village. Pony rides on ${ponyRideDatesLabel}.`;

  const structuredDescription =
    `Belvoir Animal Park is open Saturday and Sunday, and every day from ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}. Meet friendly farm animals and enjoy feeding sessions. Pony rides (11am–2pm) on ${ponyRideDatesLabel}.`;

  const ponyRideOpeningHours = PONY_RIDE_DATES.map(({ iso }) => ({
    '@type': 'OpeningHoursSpecification',
    validFrom: iso,
    validThrough: iso,
    opens: '10:00',
    closes: '16:00',
  }));

  const openingHoursSpecification = [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '10:00',
      closes: '16:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      validFrom: ANIMAL_PARK_OCTOBER_HALF_TERM_2026.start,
      validThrough: ANIMAL_PARK_OCTOBER_HALF_TERM_2026.end,
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '10:00',
      closes: '16:00',
    },
    ...ponyRideOpeningHours,
  ];

  return (
    <Page>
      <SEO
        title="Belvoir Animal Park — Family Farm Fun at Belvoir Castle"
        description={seoDescription}
        keywords="Belvoir Animal Park, farm animals, family activities, farm park, animal experiences, pony rides, Leicestershire, Belvoir Castle"
        canonicalUrl="https://www.belvoircastle.com/retail-village/animal-park"
        ogImage={animalBanner}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'TouristAttraction',
          name: 'Belvoir Animal Park',
          description: structuredDescription,
          image: animalBanner,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Belvoir Castle',
            addressLocality: 'Grantham',
            addressRegion: 'Leicestershire',
            postalCode: 'NG32 1PE',
            addressCountry: 'UK',
          },
          openingHoursSpecification,
          priceRange: '£',
          telephone: '+441476871001',
          sameAs: [
            'https://www.facebook.com/belvoircastle',
            'https://www.instagram.com/belvoircastle',
            'https://twitter.com/belvoircastle',
          ],
        }}
      />

      <ParallaxBanner
        title="Belvoir Animal Park"
        subtitle={
          openingDisplay.days === 'Daily'
            ? `Open every day ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}`
            : `Open weekends · every day ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}`
        }
        image={animalBanner}
      />

      {/* Quick-info strip */}
      <InfoStrip>
        <InfoStripInner>
          <InfoItem>
            <InfoIcon><FontAwesomeIcon icon={faCalendarAlt} /></InfoIcon>
            <InfoText>
              <strong>When we&apos;re open</strong>
              <span>
                {openingDisplay.days === 'Daily'
                  ? `Open every day ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}`
                  : `Weekends only · daily ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}`}
              </span>
            </InfoText>
          </InfoItem>
          <InfoItem>
            <InfoIcon><FontAwesomeIcon icon={faClock} /></InfoIcon>
            <InfoText>
              <strong>Opening hours</strong>
              <span>{openingDisplay.times}</span>
            </InfoText>
          </InfoItem>
          <InfoItem>
            <InfoIcon><FontAwesomeIcon icon={faTicketAlt} /></InfoIcon>
            <InfoText>
              <strong>Best value</strong>
              <span>From £4.50 per person</span>
            </InfoText>
          </InfoItem>
        </InfoStripInner>
      </InfoStrip>

      {/* Intro */}
      <IntroSection>
        <Wrap>
          <EyebrowLabel>Belvoir Castle — Retail Village</EyebrowLabel>
          <PageTitle>
            Where families fall in love<br />with farm life
          </PageTitle>
          <IntroBody>
            Nestled within the historic grounds of Belvoir Castle, our Animal Park brings
            families face to face with beautiful farm animals in a setting like no other.
            Open at weekends, and every day from {ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}.
            Feed the animals, enjoy pony
            rides on selected dates, and make the Animal Park part of a full day at the
            Castle, gardens and Retail Village.
          </IntroBody>
          <IntroCtaRow>
            <BookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Book Animal Park
            </BookBtn>
            <SecondaryBookBtn href="/your-visit/day-tickets">
              Day tickets
            </SecondaryBookBtn>
          </IntroCtaRow>
        </Wrap>
      </IntroSection>

      <StuartTeaseSection>
        <Wrap>
          <StuartTeaseWrap>
            <StuartTeaseLine>
              Stuart and his friends need your help!
            </StuartTeaseLine>
            <StuartTeaseBody>
              This October, a spooky curse has taken hold of Belvoir Animal Park. Become a Curse Breaker, follow the clues, and help free Stuart&rsquo;s friends from the magical spell.
            </StuartTeaseBody>
            <StuartTeaseBtn href="/halloween-belvoir-castle">
              Become a Curse Breaker
            </StuartTeaseBtn>
          </StuartTeaseWrap>
        </Wrap>
      </StuartTeaseSection>

      <Divider />

      {/* Experiences */}
      <ExperiencesSection>
        <Wrap>
          <SectionHeader>
            <SectionTitle>What's waiting for you</SectionTitle>
            <SectionSubtitle>
              Every visit to Belvoir Animal Park is packed with hands-on moments, fresh air,
              and the kind of joy that only farm animals can bring.
            </SectionSubtitle>
          </SectionHeader>
          <ExperiencesGrid>
            {experiences.map((exp, i) => (
              <ExperienceCard key={i}>
                <CardIcon>
                  <FontAwesomeIcon icon={exp.icon} />
                </CardIcon>
                <CardTitle>{exp.title}</CardTitle>
                <CardText>{exp.text}</CardText>
              </ExperienceCard>
            ))}
          </ExperiencesGrid>
        </Wrap>
      </ExperiencesSection>

      <Divider />

      {/* Pony ride days */}
      <MayFourthSection>
        <Wrap>
          <MayFourthInner>
            <MayFourthContent>
              <EventBadge>Special dates — pony rides</EventBadge>
              <MayFourthTitle>Pony rides at the Animal Park</MayFourthTitle>
              <MayFourthBody>
                Our ponies will be tacked up and ready for rides on Saturday 12 September
                2026 — a rare treat available exclusively on this day.
              </MayFourthBody>
              <PonyDateList>
                {PONY_RIDE_DATES.map(({ label }) => (
                  <PonyDateItem key={label}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {label}
                  </PonyDateItem>
                ))}
              </PonyDateList>
              <MayFourthDetails>
                <MayFourthDetail>
                  <FontAwesomeIcon icon={faHorse} />
                  Pony rides available 11am – 2pm
                </MayFourthDetail>
                <MayFourthDetail>
                  <FontAwesomeIcon icon={faStar} />
                  Add pony rides when you book tickets for this date
                </MayFourthDetail>
                <MayFourthDetail>
                  <FontAwesomeIcon icon={faClock} />
                  Animal Park open 10:00 – 16:00 as usual
                </MayFourthDetail>
              </MayFourthDetails>
              <PonyBookButtons>
                {PONY_RIDE_DATES.map(({ iso, label }) => (
                  <WhiteBookBtn
                    key={iso}
                    href={bookingUrl(iso)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book for {label.replace(/ \d{4}$/, '')}
                  </WhiteBookBtn>
                ))}
              </PonyBookButtons>
            </MayFourthContent>
            <MayFourthVisual>
              <MayFourthImage src={ponyImage} alt="Pony at Belvoir Animal Park" />
            </MayFourthVisual>
          </MayFourthInner>
        </Wrap>
      </MayFourthSection>

      <Divider />

      {/* Reptile Day — more dates coming soon */}
      <ReptileSection>
        <Wrap>
          <ReptileCard>
            <ReptileLogoBanner>
              <ReptileLogo
                src={REPTILE_LOGO}
                alt="Riverview Reptiles"
              />
            </ReptileLogoBanner>
            <ReptileBody>
              <ReptileBadge>Free with Animal Park admission</ReptileBadge>
              <ReptileTitle>Reptile Day with Riverview Reptiles</ReptileTitle>
              <ReptileIntro>
                We are delighted to welcome Riverview Reptiles to Belvoir Animal Park on
                selected dates throughout the season. Included at no extra charge with your
                Animal Park ticket — come face to face with fascinating reptiles in their
                own gazebo on the park.
              </ReptileIntro>
              <ReptileDetails>
                <ReptileDetail>
                  <FontAwesomeIcon icon={faDragon} />
                  Hands-on handling sessions with different reptiles
                </ReptileDetail>
                <ReptileDetail>
                  <FontAwesomeIcon icon={faTicketAlt} />
                  Free of charge — included with your Animal Park ticket
                </ReptileDetail>
              </ReptileDetails>
              <ReptileGallery>
                {REPTILE_IMAGES.map((img) => (
                  <ReptileGalleryImage key={img.src} src={img.src} alt={img.alt} />
                ))}
              </ReptileGallery>
              <ReptileComingSoon>More dates coming soon — check back for updates.</ReptileComingSoon>
            </ReptileBody>
          </ReptileCard>
        </Wrap>
      </ReptileSection>

      <Divider />

      {/* Map */}
      <MapSection>
        <Wrap>
          <SectionHeader>
            <SectionTitle>Explore the park</SectionTitle>
            <SectionSubtitle>
              Use our park map to find your favourite animals and plan your visit
              before you arrive.
            </SectionSubtitle>
          </SectionHeader>
          <MapFrame>
            <MapImage src={animalMap} alt="Belvoir Animal Park map" />
          </MapFrame>
        </Wrap>
      </MapSection>

      {/* Pricing */}
      <PricingSection>
        <Wrap>
          <SectionHeader>
            <SectionTitle>Tickets &amp; pricing</SectionTitle>
            <SectionSubtitle>
              Book Animal Park admission on its own, or make a full day of it with Castle
              or Gardens day tickets.
            </SectionSubtitle>
          </SectionHeader>
          <PricingGrid>
            <PricingCard>
              <PricingLabel>Animal Park only</PricingLabel>
              <PricingAmount>£4.50</PricingAmount>
              <PricingPer>per person</PricingPer>
              <PricingNote>Children under 3 enter free. Tickets must be booked online in advance.</PricingNote>
              <BookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Tickets
              </BookBtn>
            </PricingCard>
            <PricingCard>
              <PricingLabel>Reptile Day</PricingLabel>
              <PricingAmount style={{ fontSize: '2rem' }}>Included</PricingAmount>
              <PricingPer>dates to be announced</PricingPer>
              <PricingNote>
                Riverview Reptiles visit the Animal Park at no extra cost on selected dates.
                More dates coming soon.
              </PricingNote>
              <BookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Animal Park
              </BookBtn>
            </PricingCard>
            <PricingCard>
              <PricingLabel>Pony rides</PricingLabel>
              <PricingAmount style={{ fontSize: '2rem' }}>Add-on</PricingAmount>
              <PricingPer>{ponyRideDatesLabel}</PricingPer>
              <PricingNote>
                Select pony rides during the online booking journey when booking your
                visit for Saturday 12 September. Not available on other dates.
              </PricingNote>
              <PonyBookButtons>
                {PONY_RIDE_DATES.map(({ iso, label }) => (
                  <BookBtn
                    key={iso}
                    href={bookingUrl(iso)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book for {label.replace(/ \d{4}$/, '')}
                  </BookBtn>
                ))}
              </PonyBookButtons>
            </PricingCard>
          </PricingGrid>
        </Wrap>
      </PricingSection>

      <Footer />
    </Page>
  );
};

export default AnimalPark;
